import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Button,
  VStack,
  chakra,
  FormControl,
  FormErrorMessage,
  Input,
  FormLabel,
  Checkbox,
  Link,
  HStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  PinInput,
  PinInputField,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { FaWhatsapp, FaLock } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useProposalStore } from "@/contexts/useProposalStore";
import { usePersonalDataStore } from "@/contexts/usePersonalDataStore";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { phoneFormatter } from "@/utils/phoneFormatter";
import TextDisplay from "@/components/TextDisplay";
import { useConditionalBackground } from "@/contexts/useConditionalBackground ";
import { Formik, Field, FieldProps, useFormikContext } from "formik";
import * as Yup from "yup";
import { ProposalConsignado } from "@/models/proposalConsignado.model";
import { formatDateWithNoHours } from "@/utils/formt-date-with-no-hours";
import {
  enviarCodigoSeguranca,
  validarCodigoSeguranca,
} from "@/services/proposal.service";

interface ConsignadoPersnonalDataFormValues {
  fone: string;
  foneConfirmacao: string;
  email: string;
  emailRh: string;
  foneRh: string;
  aceite: boolean;
}

const ChakraForm = chakra("form");
interface PersistedPersonalData {
  values: ConsignadoPersnonalDataFormValues;
  aceite: boolean;
  whatsappVerified: boolean;
  savedPhone: string;
  codeEverSent: boolean;
  countdown: number;
}

function PersistPersonalDataForm({
  storageKey,
  aceite,
  whatsappVerified,
  savedPhone,
  codeEverSent,
  countdown,
}: {
  storageKey: string | null;
  aceite: boolean;
  whatsappVerified: boolean;
  savedPhone: string;
  codeEverSent: boolean;
  countdown: number;
}) {
  const { values } = useFormikContext<ConsignadoPersnonalDataFormValues>();

  useEffect(() => {
    if (!storageKey) return;

    const payload: PersistedPersonalData = {
      values,
      aceite,
      whatsappVerified,
      savedPhone,
      codeEverSent,
      countdown,
    };

    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  }, [aceite, codeEverSent, countdown, savedPhone, storageKey, values, whatsappVerified]);

  return null;
}

const validationSchema = Yup.object().shape({
  fone: Yup.string()
    .required("Telefone é obrigatório")
    .test(
      "valid-phone",
      "Telefone inválido. Digite um celular válido com DDD e o dígito 9",
      (value) => {
        if (!value) return false;
        const numbers = value.replace(/\D/g, "");
        return /^\d{2}9\d{8}$/.test(numbers);
      }
    ),
  foneConfirmacao: Yup.string()
    .required("Confirmação de telefone é obrigatória")
    .test("phones-match", "Os telefones não coincidem", function (value) {
      return value?.replace(/\D/g, "") === this.parent.fone?.replace(/\D/g, "");
    }),
  email: Yup.string().required("E-mail é obrigatório").email("E-mail inválido"),
  emailRh: Yup.string().email("E-mail inválido").optional(),
  foneRh: Yup.string()
    .optional()
    .test(
      "valid-phone-rh",
      "Telefone inválido. Digite um celular válido com DDD e o dígito 9",
      (value) => {
        if (!value || value.replace(/\D/g, "") === "") return true;
        const numbers = value.replace(/\D/g, "");
        return /^\d{2}9\d{8}$/.test(numbers);
      }
    ),
});

export default function ConsignadoPersonalData() {
  const { proposal } = useProposalStore();
  const { telefone, email, aceite, whatsappVerified, setPersonalData, setAceite, setWhatsappVerified } =
    usePersonalDataStore();
  const navigate = useNavigate();
  const backgroundStyle = useConditionalBackground();
  const { shorthash } = useParams<{ shorthash: string }>();
  const toast = useToast();
  const formStorageKey = shorthash ? `consignado:personal-data:${shorthash}` : null;

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [savedPhone, setSavedPhone] = useState("");
  const [codeEverSent, setCodeEverSent] = useState(false);

  const getPersistedData = (): PersistedPersonalData | null => {
    if (!formStorageKey) return null;
    const rawData = sessionStorage.getItem(formStorageKey);
    if (!rawData) return null;

    try {
      return JSON.parse(rawData) as PersistedPersonalData;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    setCanResend(countdown === 0);
  }, [countdown]);

  useEffect(() => {
    const persistedData = getPersistedData();
    if (!persistedData) return;

    setAceite(!!persistedData.aceite);
    setWhatsappVerified(!!persistedData.whatsappVerified);
    setSavedPhone(persistedData.savedPhone || "");
    setCodeEverSent(!!persistedData.codeEverSent);
    setCountdown(Number.isFinite(persistedData.countdown) ? persistedData.countdown : 0);
  }, [formStorageKey, setAceite, setWhatsappVerified]);

  // Countdown corre independente do modal estar aberto ou fechado
  useEffect(() => {
    if (!codeEverSent || countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [codeEverSent, countdown]);

  const persistedData = getPersistedData();
  const initialValues: ConsignadoPersnonalDataFormValues = {
    fone: persistedData?.values?.fone ?? (telefone ? phoneFormatter(telefone) : ""),
    foneConfirmacao:
      persistedData?.values?.foneConfirmacao ?? (telefone ? phoneFormatter(telefone) : ""),
    email: persistedData?.values?.email ?? (email || ""),
    emailRh: persistedData?.values?.emailRh ?? "",
    foneRh: persistedData?.values?.foneRh ?? "",
    aceite: persistedData?.values?.aceite ?? false,
  };

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleSendCode = async (phone: string) => {
    const proposalId = (proposal as ProposalConsignado)?.proposal_id;
    try {
      setIsSendingCode(true);
      await enviarCodigoSeguranca(proposalId, phone);
      setOtpCode("");
      setCountdown(60);
      setCanResend(false);
      setCodeEverSent(true);
      setShowOtpModal(true);
      toast({
        title: "Código enviado!",
        description: "Verifique seu WhatsApp.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch {
      toast({
        title: "Erro ao enviar código",
        description: "Não foi possível enviar o código de verificação.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleConfirm = (values: ConsignadoPersnonalDataFormValues) => {
    if (!aceite) return;
    const telefoneLimpo = values.fone.replace(/\D/g, "");
    setPersonalData(
      telefoneLimpo,
      values.email,
      values.emailRh || undefined,
      values.foneRh ? values.foneRh.replace(/\D/g, "") : undefined
    );
    navigate(`/${shorthash}/consignado/confirmacao`);
  };

  const handleWhatsappCode = async (values: ConsignadoPersnonalDataFormValues) => {
    const telefoneLimpo = values.fone.replace(/\D/g, "");
    setSavedPhone(telefoneLimpo);
    setWhatsappVerified(false);

    // Se ainda há cooldown ativo, só reabre o modal sem bater na API
    if (codeEverSent && countdown > 0) {
      setShowOtpModal(true);
      return;
    }

    await handleSendCode(telefoneLimpo);
  };

  const handleValidateOtp = async () => {
    if (otpCode.length < 5) return;
    const proposalId = (proposal as ProposalConsignado)?.proposal_id;
    try {
      setIsValidatingCode(true);
      await validarCodigoSeguranca(proposalId, otpCode);
      setShowOtpModal(false);
      setWhatsappVerified(true);
      toast({
        title: "WhatsApp confirmado!",
        description: "Agora clique em Confirmar Dados para continuar.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        toast({
          title: "Acesso negado",
          description: "Código inválido. Por favor, tente novamente.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } else {
        toast({
          title: "Erro ao validar código",
          description: "Não foi possível validar o código. Tente novamente.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
      setOtpCode("");
    } finally {
      setIsValidatingCode(false);
    }
  };

  const OtpModal = (
    <Modal
      isOpen={showOtpModal}
      onClose={() => setShowOtpModal(false)}
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent mx={4} borderRadius="2xl" fontFamily="Lexend Deca">
        <ModalCloseButton />
        <ModalBody p={7}>
          <VStack spacing={6} align="center">
            <Box
              bg="primary"
              p={4}
              borderRadius="xl"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaLock} color="white" fontSize="1.5rem" />
            </Box>

            <Text
              fontSize="xl"
              fontWeight="bold"
              color="gray.800"
              textAlign="center"
            >
              Inserir Código
            </Text>

            <Text fontSize="sm" color="gray.500" textAlign="center">
              Digite o código de 5 dígitos enviado para o seu WhatsApp
            </Text>

            <HStack spacing={3} justify="center">
              <PinInput
                type="number"
                value={otpCode}
                onChange={setOtpCode}
                size="lg"
                focusBorderColor="primary"
                otp
              >
                {[...Array(5)].map((_, i) => (
                  <PinInputField
                    key={i}
                    borderRadius="lg"
                    borderWidth="2px"
                    fontSize="xl"
                    fontWeight="bold"
                    h="12"
                    w="10"
                    inputMode="numeric"
                  />
                ))}
              </PinInput>
            </HStack>

            <Button
              width="100%"
              borderRadius="full"
              h="12"
              bg="primary"
              color="white"
              fontSize="md"
              fontWeight="bold"
              isDisabled={otpCode.length < 5 || isValidatingCode}
              isLoading={isValidatingCode}
              loadingText="Validando..."
              onClick={handleValidateOtp}
              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            >
              Enviar
            </Button>

            <Button
              width="100%"
              variant="ghost"
              borderRadius="full"
              h="12"
              fontSize="sm"
              color="primary"
              isDisabled={!canResend || isSendingCode}
              isLoading={isSendingCode}
              loadingText="Reenviando..."
              onClick={() => handleSendCode(savedPhone)}
              _disabled={{ opacity: 1, cursor: "default", color: "gray.500" }}
            >
              {canResend
                ? "Reenviar Código"
                : `Reenviar Código: ${formatCountdown(countdown)}`}
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  return (
    <>
      <Box
        minWidth="100vw"
        minHeight="100vh"
        {...backgroundStyle}
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        px={4}
      >
        <VStack
          mt="3rem"
          spacing={6}
          bg="white"
          p={8}
          borderRadius="lg"
          boxShadow="lg"
          width="100%"
          maxWidth="400px"
          align="start"
          color="gray.800"
          fontFamily="Lexend Deca"
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
            color="primary"
            textAlign="center"
            width="100%"
          >
            Dados Pessoais
          </Text>

          <Box width="100%">
            <Box bg="gray.50" padding="1" borderRadius="md" boxShadow="sm" mt="0">
              <VStack align="start" spacing="3">
                <TextDisplay
                  label="Nome Completo"
                  value={(proposal as ProposalConsignado)?.nome}
                />
                <TextDisplay
                  label="CPF"
                  value={cpfFormatter((proposal as ProposalConsignado)?.cpf!)}
                />
                <TextDisplay
                  label="Data de Nascimento"
                  value={formatDateWithNoHours(
                    (proposal as ProposalConsignado)?.data_nascimento
                  )}
                />
                <TextDisplay
                  label="Nome da Mãe"
                  value={(proposal as ProposalConsignado)?.nome_mae!}
                />
              </VStack>
            </Box>
          </Box>

          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            validateOnMount={true}
            validateOnChange={true}
            onSubmit={(values) => handleConfirm(values)}
          >
            {({ errors, touched, handleSubmit, isValid, isSubmitting, values }) => (
              <ChakraForm width="100%" onSubmit={handleSubmit}>
                <PersistPersonalDataForm
                  storageKey={formStorageKey}
                  aceite={aceite}
                  whatsappVerified={whatsappVerified}
                  savedPhone={savedPhone}
                  codeEverSent={codeEverSent}
                  countdown={countdown}
                />
                <Box width="100%" paddingBottom="2rem">
                  <FormControl
                    isInvalid={!!errors.email && touched.email}
                    paddingBottom="2rem"
                  >
                    <FormLabel htmlFor="email">E-mail</FormLabel>
                    <Field name="email">
                      {({ field, form }: FieldProps) => (
                        <Input
                          {...field}
                          id="email"
                          placeholder="Digite seu email"
                          type="email"
                          onChange={(e) => {
                            form.setFieldValue("email", e.target.value);
                          }}
                          fontSize="lg"
                          width="100%"
                          variant="flushed"
                          focusBorderColor="primary"
                          _placeholder={{ color: "primary.500" }}
                          paddingY="1rem"
                        />
                      )}
                    </Field>
                    <FormErrorMessage marginTop="0.5rem">
                      {errors.email}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.fone && touched.fone}
                    paddingBottom="2rem"
                  >
                    <FormLabel htmlFor="fone">
                      <HStack spacing={2}>
                        <Text as="span">Telefone WhatsApp</Text>
                        <Icon as={FaWhatsapp} color="green.500" boxSize={5} />
                      </HStack>
                    </FormLabel>
                    <Field name="fone">
                      {({ field, form }: FieldProps) => (
                        <Input
                          {...field}
                          id="fone"
                          placeholder="Digite seu WhatsApp"
                          type="tel"
                          isDisabled={whatsappVerified || !canResend}
                          value={phoneFormatter(field.value || "")}
                          onChange={(e) => {
                            const formatted = phoneFormatter(e.target.value);
                            form.setFieldValue("fone", formatted);
                          }}
                          fontSize="lg"
                          width="100%"
                          variant="flushed"
                          _placeholder={{ color: "primary.500" }}
                          paddingY="1rem"
                        />
                      )}
                    </Field>
                    <FormErrorMessage marginTop="0.5rem">
                      {errors.fone}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={
                      !!errors.foneConfirmacao && touched.foneConfirmacao
                    }
                    paddingBottom="2rem"
                  >
                    <FormLabel htmlFor="foneConfirmacao">
                      <HStack spacing={2}>
                        <Text as="span">Confirme WhatsApp</Text>
                        <Icon as={FaWhatsapp} color="green.500" boxSize={5} />
                      </HStack>
                    </FormLabel>
                    <Field name="foneConfirmacao">
                      {({ field, form }: FieldProps) => (
                        <Input
                          {...field}
                          id="foneConfirmacao"
                          placeholder="Confirme seu WhatsApp"
                          type="tel"
                          isDisabled={whatsappVerified || !canResend}
                          value={phoneFormatter(field.value || "")}
                          onChange={(e) => {
                            const formatted = phoneFormatter(e.target.value);
                            form.setFieldValue("foneConfirmacao", formatted);
                          }}
                          fontSize="lg"
                          width="100%"
                          variant="flushed"
                          _placeholder={{ color: "primary.500" }}
                          paddingY="1rem"
                        />
                      )}
                    </Field>
                    <FormErrorMessage marginTop="0.5rem">
                      {errors.foneConfirmacao}
                    </FormErrorMessage>

                    <Button
                      width="100%"
                      borderRadius="full"
                      type="button"
                      height="auto"
                      minHeight="3rem"
                      isDisabled={
                        !!errors.fone ||
                        !!errors.foneConfirmacao ||
                        !values.fone ||
                        !values.foneConfirmacao ||
                        isSendingCode ||
                        whatsappVerified
                      }
                      isLoading={isSendingCode}
                      loadingText="Enviando código..."
                      marginTop="1.5rem"
                      fontSize="md"
                      paddingY="3"
                      paddingX="4"
                      whiteSpace="normal"
                      variant="outline"
                      borderColor="green.500"
                      color="green.600"
                      _hover={{ bg: "green.50" }}
                      _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
                      leftIcon={<Icon as={FaWhatsapp} color="green.500" flexShrink={0} />}
                      onClick={() => handleWhatsappCode(values)}
                    >
                      Confirmar e Receber Código no WhatsApp
                    </Button>
                  </FormControl>

                  <Divider borderColor="gray.200" opacity={0.6} my={2} />

                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color="primary"
                    textAlign="center"
                    width="100%"
                    mt={2}
                    mb={4}
                  >
                    Dados da empresa
                  </Text>

                  <FormControl
                    isInvalid={!!errors.emailRh && touched.emailRh}
                    paddingBottom="2rem"
                  >
                    <FormLabel htmlFor="emailRh">
                      E-mail do responsável pelo RH{" "}
                      <Text as="span" fontSize="xs" color="gray.400" fontWeight="normal">
                        (opcional)
                      </Text>
                    </FormLabel>
                    <Field name="emailRh">
                      {({ field, form }: FieldProps) => (
                        <Input
                          {...field}
                          id="emailRh"
                          placeholder="Digite o e-mail"
                          type="email"
                          onChange={(e) => {
                            form.setFieldValue("emailRh", e.target.value);
                          }}
                          fontSize="lg"
                          width="100%"
                          variant="flushed"
                          focusBorderColor="primary"
                          _placeholder={{ color: "gray.300" }}
                          paddingY="1rem"
                        />
                      )}
                    </Field>
                    <FormErrorMessage marginTop="0.5rem">
                      {errors.emailRh}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!errors.foneRh && touched.foneRh}
                    paddingBottom="2rem"
                  >
                    <FormLabel htmlFor="foneRh">
                      Telefone do responsável pelo RH{" "}
                      <Text as="span" fontSize="xs" color="gray.400" fontWeight="normal">
                        (opcional)
                      </Text>
                    </FormLabel>
                    <Field name="foneRh">
                      {({ field, form }: FieldProps) => (
                        <Input
                          {...field}
                          id="foneRh"
                          placeholder="Digite o telefone"
                          type="tel"
                          value={phoneFormatter(field.value || "")}
                          onChange={(e) => {
                            const formatted = phoneFormatter(e.target.value);
                            form.setFieldValue("foneRh", formatted);
                          }}
                          fontSize="lg"
                          width="100%"
                          variant="flushed"
                          _placeholder={{ color: "gray.300" }}
                          paddingY="1rem"
                        />
                      )}
                    </Field>
                    <FormErrorMessage marginTop="0.5rem">
                      {errors.foneRh}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl paddingBottom="2rem">
                    <Checkbox
                      isChecked={aceite}
                      onChange={(e) => setAceite(e.target.checked)}
                      colorScheme="whiteLabeColor"
                      size="md"
                    >
                      <Text fontSize="sm" color="gray.700">
                        Eu aceito os{" "}
                        <Link
                          href={
                            (proposal as ProposalConsignado)
                              ?.link_consent_term || ""
                          }
                          target="_blank"
                          textDecoration="underline"
                          textDecorationColor="whiteLabeColor.500"
                          color="whiteLabeColor.500"
                          fontWeight="semibold"
                        >
                          termos de autorização.
                        </Link>
                      </Text>
                    </Checkbox>
                  </FormControl>



                  <Button
                    width="100%"
                    borderRadius="24px"
                    type="submit"
                    height="3rem"
                    isDisabled={!aceite || !isValid || isSubmitting || !whatsappVerified}
                    isLoading={isSubmitting}
                    marginTop="1rem"
                    fontSize="lg"
                    paddingY="1.5rem"
                    bg={aceite && isValid && whatsappVerified ? "primary" : "gray.300"}
                    color={aceite && isValid && whatsappVerified ? "white" : "gray.500"}
                    _disabled={{
                      opacity: 1,
                      cursor: "not-allowed",
                    }}
                  >
                    Confirmar Dados
                  </Button>
                </Box>
              </ChakraForm>
            )}
          </Formik>
        </VStack>
      </Box>

      {OtpModal}
    </>
  );
}
